import { Component } from './component.js';

/**
 * Announcement Bar Component
 * Handles automatic slideshow of announcements with fade transitions
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    // Pause on hover
    this.addEventListener('mouseenter', this.suspend.bind(this));
    this.addEventListener('mouseleave', this.resume.bind(this));
    
    // Pause when page is hidden
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Start autoplay
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Navigate to next slide
   */
  next() {
    this.current += 1;
  }

  /**
   * Navigate to previous slide
   */
  previous() {
    this.current -= 1;
  }

  /**
   * Start automatic playback
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  /**
   * Pause playback
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /**
   * Stop interval
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resume playback
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  /**
   * Get/Set current slide index
   */
  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides || [];
    let relativeIndex = current % slides.length;
    
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Check if autoplay is enabled
   */
  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  /**
   * Get autoplay interval in milliseconds
   */
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * Get/Set paused state
   */
  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Handle visibility change
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  };
}

// Define custom element
if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

// Define slideshow-arrows custom element
if (!customElements.get('slideshow-arrows')) {
  customElements.define('slideshow-arrows', class extends HTMLElement {});
}

// Define slideshow-slide custom element
if (!customElements.get('slideshow-slide')) {
  customElements.define('slideshow-slide', class extends HTMLElement {});
}
